import { personMutatorKeyMatcher, useLogin, UserLogon } from '@/data/Content/Login';
import {
	Box,
	Divider,
	IconButton,
	List,
	ListItem,
	Modal,
	Paper,
	Stack,
	Typography,
} from '@mui/material';
import { useSWRConfig } from 'swr';
import CloseIcon from '@mui/icons-material/Close';
import {
	ModalLoginContentSx,
	ModalLoginFrameSx,
	ModalLoginHeaderIconCloseFrameSx,
	ModalLoginHeaderIconCloseSx,
	ModalLoginRegisterContentSx,
	ModalLoginRegisterTextSx,
	ModalLoginRegisterTitleSx,
	ModalLoginTitleSx,
} from '@/components/content/Cart/parts/ModalLogin/styles/main';
import { SingleLoginForm } from '@/components/blocks/SingleLoginForm';
import { DpBaseButton } from '@/components/blocks/DpBaseButton';
import { useRecaptchaV3Setup } from '@/data/Content/Recaptcha';
import { useSuccessLoginNotify } from '@/utils/useSuccessLoginNotify';
import { useReplaceAuthRoute } from 'integration/hooks/useReplaceAuthRoute';

interface ModalLoginProps {
	type?: 'login' | 'wishlist';
	onClose: () => void;
	onGoRegister: () => void;
}

export const ModalLogin: React.FC<ModalLoginProps> = ({
	type = 'login',
	onClose,
	onGoRegister,
}) => {
	const { mutate } = useSWRConfig();
	const { loginSubmit } = useLogin();
	const { recaptchaEnabled, recaptchaExecute, recaptchaLoading } = useRecaptchaV3Setup();
	const {showSuccessAuthUser} = useSuccessLoginNotify();
	const { onReplaceAuthRoute } = useReplaceAuthRoute({ shouldCallToModal: true });

	const handleLogin = async (props: UserLogon) => {
		let x_recaptchaToken: string | undefined = undefined;

		if (recaptchaEnabled) {
			x_recaptchaToken = await recaptchaExecute('login');
		}

		const resp = await loginSubmit({ ...props, x_recaptchaToken });

		if ('userId' in (resp ?? {})) {
			await onReplaceAuthRoute();
			await mutate(personMutatorKeyMatcher(''), undefined);
			await showSuccessAuthUser();
			onClose();
		}
	};

	return (
		<Modal open onClose={onClose}>
			<Paper sx={ModalLoginFrameSx}>
				<Stack direction="row" justifyContent="flex-end" width="100%">
					<IconButton onClick={onClose} sx={ModalLoginHeaderIconCloseFrameSx}>
						<CloseIcon sx={ModalLoginHeaderIconCloseSx} />
					</IconButton>
				</Stack>
				<Typography sx={ModalLoginTitleSx}>
					{type === 'wishlist'
						? 'Crea una lista con tus productos favoritos'
						: '¿CÓMO QUIERES CONTINUAR TU COMPRA?'}
				</Typography>
				<Box sx={ModalLoginContentSx}>
					<Box>
						<Typography sx={ModalLoginRegisterTitleSx}>{'INICIAR SESIÓN'}</Typography>
						<SingleLoginForm
							signInButtonLabel={type === 'wishlist' ? 'Iniciar sesión' : 'Comprar con mi cuenta'}
							disabled={recaptchaLoading}
							forgotPasswordPosition="form"
							onSubmit={handleLogin}
						/>
					</Box>
					<Divider orientation="vertical" />
					<Stack sx={ModalLoginRegisterContentSx}>
						<Box>
							<Typography sx={{ ...ModalLoginRegisterTitleSx, mb: '14px' }}>
								{'CREAR CUENTA'}
							</Typography>
							<Typography sx={ModalLoginRegisterTextSx}>
								{
									'Regístrate super rápido y recibe todos los beneficios de ser un usuario dportenis.'
								}
							</Typography>

							<List sx={{ ...ModalLoginRegisterTextSx, listStyleType: 'disc', pl: 2 }}>
								<ListItem sx={{ display: 'list-item', p: 0 }}>
									<Typography variant="body1">
										{'Promociones exclusivas y acumula puntos al comprar.'}
									</Typography>
								</ListItem>
								<ListItem sx={{ display: 'list-item', p: 0 }}>
									<Typography variant="body1">
										{'Ve el seguimiento a tu pedido en todo momento.'}
									</Typography>
								</ListItem>
								<ListItem sx={{ display: 'list-item', p: 0 }}>
									<Typography variant="body1">
										{'Realiza devolución o cambio, de manera rápida y fácil.'}
									</Typography>
								</ListItem>
							</List>
						</Box>
						<DpBaseButton
							sx={{
								mb: '20px',
								'&:hover': {
									backgroundColor: '#E8E8E8',
								},
							}}
							variant="secondary"
							onClick={onGoRegister}
						>
							{'Me quiero registrar'}
						</DpBaseButton>
					</Stack>
				</Box>
			</Paper>
		</Modal>
	);
};
